import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import type { RepartoMes } from '../../hooks/useCalendarioMes'
import { useEliminarReparto, useMoverReparto } from '../../hooks/useCalendarioMes'
import { COLORES_DIA_LISTA } from '../agendamiento/coloresDia'
import { marcaDeEstado } from '../agendamiento/estadoReparto'
import { Modal } from '../../componentes/Modal'
import { PerfilCliente } from '../../componentes/PerfilCliente'
import { IconoCliente, IconoConversaciones, IconoAgendamiento, IconoFactura, IconoQuitar } from '../../componentes/iconos'
import { RutaDelDia } from './RutaDelDia'

/**
 * Modal del DÍA seleccionado en el calendario mensual (opción "ver + añadir").
 *
 * Al pulsar una celda del mes se abre este modal (no un panel lateral que crece
 * y mete scroll de página): los repartos van AGRUPADOS POR RUTA y el contenido
 * scrollea dentro del modal. Botón para añadir uno nuevo. Es presentacional:
 * recibe la lista y los callbacks; la página decide el alta de reparto (B.3).
 */

interface Props {
  /** Fecha ISO del día abierto (YYYY-MM-DD). */
  iso: string
  /** Repartos de ese día. */
  repartos: RepartoMes[]
  /** Añadir un reparto nuevo en este día. */
  onAnadir: (iso: string) => void
  /** Cerrar el modal. */
  onCerrar: () => void
}

/** Fecha ISO → texto legible "lunes, 15 de septiembre de 2026". */
function fechaLegible(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const fecha = new Date(y, m - 1, d)
  return fecha.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function FilaReparto({ reparto, onAbrir }: { reparto: RepartoMes; onAbrir: (r: RepartoMes) => void }) {
  const color = reparto.ruta
    ? COLORES_DIA_LISTA.find((c) => c.clave === reparto.ruta)
    : undefined
  const marca = marcaDeEstado(reparto.estado)
  return (
    <li>
      <button
        type="button"
        onClick={() => onAbrir(reparto)}
        className="flex w-full items-center gap-2 rounded-md border border-borde-suave bg-lienzo px-2.5 py-2 text-left transition-colors hover:border-acento hover:bg-elevado focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
      >
        <span
          aria-hidden
          className={['inline-block h-2.5 w-2.5 shrink-0 rounded-sm ring-1', color?.fondo ?? 'bg-superficie', color?.anillo ?? 'ring-borde'].join(' ')}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-tinta">{reparto.cliente}</p>
          <p className="flex flex-wrap gap-x-2 text-xs text-tinta-apagada">
            {reparto.bono ? <span>{reparto.bono}</span> : null}
            {reparto.ruta ? <span className="uppercase">{reparto.ruta}</span> : null}
          </p>
        </div>
        {reparto.notaEntrega ? (
          <span
            className="flex shrink-0 items-center text-aviso-icono"
            title={`Nota de entrega: ${reparto.notaEntrega}`}
            aria-label={`Nota de entrega: ${reparto.notaEntrega}`}
          >
            <IconoFactura className="size-4" />
          </span>
        ) : null}
        <span className="flex shrink-0 items-center gap-1 text-xs text-tinta-2">
          <span aria-hidden>{marca.simbolo}</span>
          {marca.etiqueta}
        </span>
      </button>
    </li>
  )
}

/** Fecha ISO → "mié 11 nov" corto, para el próximo envío. */
function fechaCorta(iso: string | null | undefined): string | null {
  if (!iso) return null
  const [y, m, d] = iso.split('-').map(Number)
  const fecha = new Date(y, m - 1, d)
  if (Number.isNaN(fecha.getTime())) return null
  return fecha.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })
}

const eur = (n: number) => `${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

/**
 * Popover de detalle de un reparto (opción B): al pulsar un reparto del día se
 * abre esta tarjeta centrada con qué pidió, cuándo vuelve a tener envío y los
 * botones Ver perfil / Ver chat. Se cierra al pulsar fuera o la ✕.
 */
function PopoverReparto({
  reparto,
  onCerrar,
  onVerPerfil,
  onVerChat,
  onEliminar,
  eliminando,
  onMover,
  moviendo,
}: {
  reparto: RepartoMes
  onCerrar: () => void
  onVerPerfil: () => void
  onVerChat: () => void
  onEliminar: () => void
  eliminando: boolean
  onMover: (fecha: string) => void
  moviendo: boolean
}) {
  const [confirmando, setConfirmando] = useState(false)
  const [moviendoUI, setMoviendoUI] = useState(false)
  const [nuevaFecha, setNuevaFecha] = useState(reparto.fecha)
  const marca = marcaDeEstado(reparto.estado)
  const lineas = reparto.lineas ?? []
  const prox = fechaCorta(reparto.proximoEnvio)
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onCerrar}
      role="presentation"
    >
      <div
        role="dialog"
        aria-label={`Reparto de ${reparto.cliente}`}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm overflow-hidden rounded-xl border border-borde-fuerte bg-superficie shadow-2xl"
      >
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-3 border-b border-borde px-4 py-3">
          <div className="min-w-0">
            <h4 className="truncate text-sm font-semibold text-tinta">{reparto.cliente}</h4>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-tinta-apagada">
              {reparto.ruta ? <span className="uppercase">{reparto.ruta}</span> : null}
              <span className="inline-flex items-center gap-1">
                <span aria-hidden>{marca.simbolo}</span>
                {marca.etiqueta}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo"
          >
            ✕
          </button>
        </div>

        {/* Qué pidió */}
        <div className="border-b border-borde px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Qué pidió</p>
          {lineas.length > 0 ? (
            <ul className="flex flex-col gap-0.5">
              {lineas.map((l, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-tinta-2">
                    {l.cantidad > 1 ? `${l.cantidad}× ` : ''}{l.nombre}
                  </span>
                  <span className="shrink-0 text-xs text-acento-tinta">
                    {l.precio > 0 ? eur(l.precio) : '—'}
                  </span>
                </li>
              ))}
              {reparto.total ? (
                <li className="mt-1.5 flex items-baseline justify-between gap-3 border-t border-borde pt-1.5 text-sm font-bold text-tinta">
                  <span>Total</span>
                  <span className="tabular-nums">{eur(reparto.total)}</span>
                </li>
              ) : null}
            </ul>
          ) : (
            <p className="text-sm text-tinta-apagada">Sin detalle de líneas.</p>
          )}
        </div>

        {/* Nota de entrega del cliente (indicación que dejó al confirmar). Se
            resalta en ámbar porque es lo que el repartidor DEBE tener en cuenta. */}
        {reparto.notaEntrega ? (
          <div className="border-b border-borde px-4 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Nota de entrega</p>
            <div className="flex items-start gap-2.5 rounded-lg border border-aviso-borde bg-aviso-fondo px-3 py-2">
              <IconoFactura className="mt-0.5 size-4 shrink-0 text-aviso-icono" />
              <p className="min-w-0 text-sm font-medium text-aviso-tinta">{reparto.notaEntrega}</p>
            </div>
          </div>
        ) : null}

        {/* Próximo envío */}
        <div className="border-b border-borde px-4 py-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-tinta-apagada">Próximo envío</p>
          {prox ? (
            <div className="flex items-center gap-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-acento-suave text-acento">
                <IconoAgendamiento className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium capitalize text-tinta">{prox}</p>
                <p className="text-xs text-tinta-apagada">
                  según su ciclo{reparto.semanaCiclo ? ` (${reparto.ruta?.toUpperCase()} · ${reparto.semanaCiclo.toUpperCase()})` : ''} · en 4 semanas
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-tinta-apagada">No se pudo calcular.</p>
          )}
        </div>

        {/* Botones */}
        <div className="flex gap-2 px-4 py-3">
          <button
            type="button"
            onClick={onVerPerfil}
            disabled={!reparto.contactId}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-semibold text-tinta transition-colors hover:border-acento disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconoCliente className="size-4" />
            Ver perfil
          </button>
          <button
            type="button"
            onClick={onVerChat}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-acento px-3 py-2 text-sm font-semibold text-tinta-inversa transition-[filter] hover:brightness-105"
          >
            <IconoConversaciones className="size-4" />
            Ver chat
          </button>
        </div>

        {/* Mover de día (Opción A: botón discreto + selector de fecha en línea).
            Cambia la fecha del albarán REAL en Holded; el calendario se refresca. */}
        {moviendoUI ? (
          <div className="border-t border-borde px-4 py-3">
            <p className="mb-2 text-sm font-semibold text-tinta">Mover a otro día</p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={nuevaFecha}
                onChange={(e) => setNuevaFecha(e.target.value)}
                disabled={moviendo}
                className="flex-1 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo disabled:opacity-50"
                aria-label="Nueva fecha del reparto"
              />
              <button
                type="button"
                onClick={() => setMoviendoUI(false)}
                disabled={moviendo}
                className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-semibold text-tinta transition-colors hover:border-acento disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => onMover(nuevaFecha)}
                disabled={moviendo || !nuevaFecha || nuevaFecha === reparto.fecha}
                className="rounded-lg bg-acento px-3 py-2 text-sm font-semibold text-acento-contraste transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {moviendo ? 'Moviendo…' : 'Mover'}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-borde px-4 py-2.5">
            <button
              type="button"
              onClick={() => { setNuevaFecha(reparto.fecha); setMoviendoUI(true) }}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-lienzo"
            >
              <IconoAgendamiento className="size-4" />
              Mover de día
            </button>
          </div>
        )}

        {/* Eliminar reparto (Opción A: botón discreto + confirmación en línea).
            Borra el albarán REAL en Holded; irreversible. */}
        {confirmando ? (
          <div className="border-t border-critico-anillo bg-critico-fondo px-4 py-3">
            <p className="mb-1 text-sm font-semibold text-critico-tinta">¿Eliminar este reparto?</p>
            <p className="mb-3 text-xs text-tinta-2">
              Se borrará el albarán de <b>{reparto.cliente}</b> en Holded. No se puede deshacer.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                disabled={eliminando}
                className="flex-1 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-semibold text-tinta transition-colors hover:border-acento disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={onEliminar}
                disabled={eliminando}
                className="flex-1 rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-2 text-sm font-semibold text-critico-tinta transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {eliminando ? 'Eliminando…' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        ) : (
          <div className="border-t border-borde px-4 py-2.5">
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-critico-tinta transition-colors hover:bg-critico-fondo"
            >
              <IconoQuitar className="size-4" />
              Eliminar reparto
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export function PanelDia({ iso, repartos, onAnadir, onCerrar }: Props) {
  const carga = repartos.length
  // Un día = una sola ruta, no se desglosa. Solo se ordena por nombre de cliente.
  const repartosOrdenados = useMemo(
    () => [...repartos].sort((a, b) => a.cliente.localeCompare(b.cliente, 'es')),
    [repartos],
  )
  const navegar = useNavigate()
  const [abierto, setAbierto] = useState<RepartoMes | null>(null)
  const [perfilId, setPerfilId] = useState<string | null>(null)
  const eliminar = useEliminarReparto()
  const mover = useMoverReparto()

  const verChat = (r: RepartoMes) => {
    setAbierto(null)
    navegar(`/conversaciones?tipo=clientes&buscar=${encodeURIComponent(r.cliente)}`)
  }
  const verPerfil = (r: RepartoMes) => {
    if (!r.contactId) return
    setAbierto(null)
    setPerfilId(r.contactId)
  }

  return (
    <Modal titulo={`Repartos del ${fechaLegible(iso)}`} onCerrar={onCerrar} anchoMax="max-w-6xl">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 border-b border-borde px-5 py-4">
        <div>
          <h3 className="text-base font-semibold capitalize text-tinta">{fechaLegible(iso)}</h3>
          <p className="mt-0.5 flex items-center gap-2 text-xs text-tinta-apagada">
            {carga > 0 ? (
              <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-acento px-1.5 py-0.5 text-xs font-bold text-acento-contraste">
                {carga}
              </span>
            ) : null}
            {carga === 0 ? 'Sin repartos' : carga === 1 ? 'reparto' : 'repartos'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar el día"
          className="rounded-md px-2 py-1 text-sm text-tinta-2 ring-1 ring-borde hover:bg-lienzo focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          ✕
        </button>
      </div>

      {/* Cuerpo: scrollea DENTRO del modal (la página no se mueve). Un día del
          calendario ES un único día de la semana = una sola ruta, así que NO se
          desglosa por R1..R5 (eso no tenía sentido). Lista limpia de clientes de
          ese día, ordenada por nombre, en rejilla para que quepan muchos. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* Ruta del día (trazabilidad): si se envió la ruta, se muestra arriba. */}
        <RutaDelDia iso={iso} />
        {carga > 0 ? (
          <ul className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {repartosOrdenados.map((r) => (
              <FilaReparto key={r.id} reparto={r} onAbrir={setAbierto} />
            ))}
          </ul>
        ) : (
          <p className="rounded-md bg-lienzo px-3 py-6 text-center text-sm text-tinta-apagada">
            No hay repartos agendados este día.
          </p>
        )}
      </div>

      {/* Pie fijo con la acción */}
      <div className="border-t border-borde px-5 py-4">
        <button
          type="button"
          onClick={() => onAnadir(iso)}
          className="w-full rounded-lg bg-acento px-3 py-2.5 text-sm font-medium text-acento-contraste hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco"
        >
          + Añadir reparto
        </button>
      </div>

      {/* Popover de detalle del reparto pulsado (opción B). */}
      {abierto ? (
        <PopoverReparto
          reparto={abierto}
          onCerrar={() => setAbierto(null)}
          onVerPerfil={() => verPerfil(abierto)}
          onVerChat={() => verChat(abierto)}
          eliminando={eliminar.isPending}
          onEliminar={() =>
            eliminar.mutate(
              { waybillId: abierto.id },
              { onSuccess: () => setAbierto(null) },
            )
          }
          moviendo={mover.isPending}
          onMover={(fecha) =>
            mover.mutate(
              { waybillId: abierto.id, fecha },
              { onSuccess: () => setAbierto(null) },
            )
          }
        />
      ) : null}

      {/* Perfil 360º abierto desde el botón "Ver perfil" del popover. */}
      <PerfilCliente contactId={perfilId} onCerrar={() => setPerfilId(null)} />
    </Modal>
  )
}
